import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import { Select } from 'components/FormElements'
// constants
import { Forms } from 'constants/forms'
// store
import { responseTypesSelector } from 'store/reducers/riskTemplates'
// types
import { IOption } from 'components/FormElements/Select'
import { IField, IInjuryMasterIncidentProperty } from 'types/graphql'
// views
import { Row, ValueMapWrapper } from './views'

interface Props {
  name: string
  property: IInjuryMasterIncidentProperty
  sourceField: IField
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

// A boolean read from a pick list: one select per Injury Master value (true, false), each picking the
// pick list option that stands for it. Kept in the same valueMap as a choice: option id -> code.
const BooleanMap: React.FC<Props> = ({ name, property, sourceField }) => {
  const dispatch = useDispatch()
  const responseTypeSets = useSelector(responseTypesSelector)

  const valueMap: Record<string, IOption | null> | undefined = useSelector((state) =>
    selector(state, `${name}.valueMap`)
  )

  // A pick list using a response type keeps its options in the company's response type set
  const fieldOptions = sourceField.responseType
    ? responseTypeSets.find((item) => item.id === sourceField.responseTypeId)?.options
    : sourceField.options

  const options: IOption[] = (fieldOptions ?? []).map(({ id, label }) => ({ value: id, label }))

  // Points the chosen option at `code`; the option chosen for it before is unmapped.
  const onChange = (code: string, selected: IOption | null) => {
    options.forEach((option) => {
      const field = `${name}.valueMap.${option.value}`

      if (option.value === selected?.value) {
        dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, field, { value: code, label: code }))
      } else if (valueMap?.[option.value]?.value === code) {
        dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, field, null))
      }
    })
  }

  return (
    <ValueMapWrapper>
      <Row>
        {(property.choices ?? []).map(({ value, label }) => {
          const code = String(value)

          return (
            <Select
              key={code}
              isClearable
              menuPortalTarget={null}
              label={`Option sent as ${label}`}
              options={options}
              value={options.find((option) => valueMap?.[option.value]?.value === code) ?? null}
              handleChange={(selected) => onChange(code, selected as IOption | null)}
            />
          )
        })}
      </Row>
    </ValueMapWrapper>
  )
}

export default BooleanMap
